import React from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Trophy, Shield, ArrowRight, CheckCircle2, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const iconMap = {
  Trophy: Trophy,
  Shield: Shield
};

const ProgramsComparison = ({ programs }) => {
  const { language } = useLanguage();
  
  const getLocalizedPath = (path) => {
    if (language === 'es') return `/es${path}`;
    if (language === 'fr') return `/fr${path}`;
    return path;
  };

  // Get translated program data
  const getTranslatedProgram = (program) => {
    const programKey = program.id === 'clinic' ? 'clinic' : 'astonVilla';
    const translatedData = getTranslation(language, `programs.${programKey}`);
    
    if (translatedData && typeof translatedData === 'object') {
      return {
        ...program,
        name: translatedData.name || program.name,
        tagline: translatedData.tagline || program.tagline,
        highlights: translatedData.highlights || program.highlights
      };
    }
    return program;
  };

  return (
    <section id="programs" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
            {getTranslation(language, 'programs.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {getTranslation(language, 'programs.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {programs.map((program, index) => {
            const translatedProgram = getTranslatedProgram(program);
            const Icon = iconMap[program.icon];
            const isComingSoon = program.comingSoon;
            
            return (
              <Card 
                key={program.id}
                className={`relative overflow-hidden group hover:shadow-2xl transition-all duration-300 flex flex-col ${
                  isComingSoon ? 'opacity-95' : 'transform hover:-translate-y-2'
                }`}
              >
                {/* Color accent bar */}
                <div className={`h-2 ${
                  program.color === 'green' ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gradient-to-r from-purple-800 to-red-700'
                }`}></div>

                {isComingSoon && (
                  <div className="absolute top-6 right-6 z-10">
                    <div className="bg-yellow-400 text-gray-900 px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider shadow-lg">
                      {getTranslation(language, 'programs.comingSoon')}
                    </div>
                  </div>
                )}

                <div className="p-8 flex flex-col flex-grow">
                  {/* Header - Fixed minimum height for alignment */}
                  <div className="flex items-start mb-6 min-h-[100px]">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0 ${
                      program.color === 'green' ? 'bg-green-100' : 'bg-gradient-to-br from-purple-100 to-red-100'
                    }`}>
                      <Icon className={`h-8 w-8 ${
                        program.color === 'green' ? 'text-green-600' : 'text-purple-800'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-2">
                        {translatedProgram.name}
                      </h3>
                      <p className="text-gray-600 font-medium">{translatedProgram.tagline}</p>
                    </div>
                  </div>

                  {/* Price - Fixed height for alignment */}
                  <div className="mb-6 h-[60px] flex items-center">
                    <div className="flex items-baseline">
                      <span className="text-sm font-bold text-gray-500 mr-2">{getTranslation(language, 'programs.from')}</span>
                      <span className={`text-4xl font-black ${
                        program.color === 'green' ? 'text-green-600' : 'text-purple-800'
                      }`}>
                        {program.priceFrom}
                      </span>
                    </div>
                  </div>

                  {/* Highlights */}
                  <div className="space-y-3 mb-8 flex-grow">
                    {translatedProgram.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-start">
                        <CheckCircle2 className={`h-5 w-5 mr-3 flex-shrink-0 mt-0.5 ${
                          program.color === 'green' ? 'text-green-600' : 'text-purple-800'
                        }`} />
                        <span className="text-gray-700 text-sm">{highlight}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button - Now at the bottom */}
                  <div className="mt-auto">
                    <Link to={getLocalizedPath(`/${program.slug}`)}>
                      <Button 
                        className={`w-full font-bold text-base py-6 ${
                          program.color === 'green'
                            ? 'bg-green-600 hover:bg-green-700 text-white'
                            : isComingSoon
                            ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                            : 'bg-gradient-to-r from-purple-800 to-red-700 hover:from-purple-900 hover:to-red-800 text-white'
                        }`}
                        disabled={isComingSoon}
                      >
                        {isComingSoon ? (
                          <>
                            <Clock className="mr-2 h-5 w-5" />
                            {getTranslation(language, 'programs.learnMore')}
                          </>
                        ) : (
                          <>
                            {getTranslation(language, 'programs.viewDetails')}
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </>
                        )}
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProgramsComparison;